import Rollbar, { Payload } from "rollbar";

import { ExpectedError } from "@utils/error-utils/errors";

interface ExtendedPayload extends Payload {
  custom?: {
    fingerprint?: string;
    context?: string;
    isExpectedError?: boolean;
  };
}

export type RollbarLogArgument = Rollbar.LogArgument;
type CustomData = Record<string, unknown>;

const transformPayload = (payload: ExtendedPayload): void => {
  payload.custom?.fingerprint && (payload.fingerprint = payload.custom.fingerprint);
  payload.custom?.context && (payload.context = payload.custom.context);

  if (
    payload.custom?.isExpectedError &&
    payload.level &&
    (["warning", "error"] as string[]).includes(payload.level as string)
  ) {
    payload.level = "info";
  }
};

const config = {
  accessToken: process.env.GATSBY_ROLLBAR_ACCESS_TOKEN,
  environment: process.env.NODE_ENV,
  captureUncaught: true,
  captureUnhandledRejections: true,
  payload: {
    environment: process.env.NODE_ENV,
    client: {
      javascript: {
        code_version: `build-${new Date().toISOString().split("T")[0]}`,
        source_map_enabled: true,
        guess_uncaught_frames: true,
      },
    },
  },
  transform: transformPayload,
  uncaughtErrorLevel: "warning" as Rollbar.Level,
  maxItems: 100,
  hostSafeList: process.env.GATSBY_URL_BASE ? [process.env.GATSBY_URL_BASE] : [],
  checkIgnore: (isUncaught: boolean, args: string[], payload: Payload): boolean => {
    if (payload.body?.message?.includes("404")) return true;
    if (payload.body?.message?.includes("not found")) return true;
    if (payload.body?.message?.includes("Outbrain")) return true;
    if (payload.body?.message?.includes("ECONNRESET")) return true;

    if (
      payload.body?.trace?.frames?.some((frame) => frame.filename?.includes("chrome-extension://"))
    ) {
      return true;
    }

    if (
      payload.body?.trace?.frames?.some(
        (frame) =>
          frame.filename &&
          !frame.filename.includes(window.location.hostname) &&
          !frame.filename.startsWith("/")
      )
    ) {
      return true;
    }

    return false;
  },
};

export const rollbar = new Rollbar(config);

export const logDebug = (
  message: string,
  error?: Error,
  customData: CustomData = {}
): Rollbar.LogResult => rollbar.debug(message, error, customData);

export const logInfo = (
  message: string,
  error?: Error,
  customData: CustomData = {}
): Rollbar.LogResult => rollbar.info(message, error, customData);

export const logWarning = (
  message: string,
  error?: Error,
  customData: CustomData = {}
): Rollbar.LogResult => {
  if (error && error instanceof ExpectedError) {
    return rollbar.warning(message, error, {
      ...customData,
      isExpectedError: true,
    });
  }

  return rollbar.warning(message, error, customData);
};

export const logError = (
  message: string,
  error?: Error,
  customData: CustomData = {}
): Rollbar.LogResult => {
  if (error && error instanceof ExpectedError) {
    return rollbar.error(message, error, {
      ...customData,
      isExpectedError: true,
    });
  }

  return rollbar.error(message, error, customData);
};

export const logCritical = (
  message: string,
  error?: Error,
  customData: CustomData = {}
): Rollbar.LogResult => rollbar.critical(message, error, customData);

export const fingerprint = (key: string): CustomData => ({
  fingerprint: key,
});
